import { useNavigate } from 'react-router-dom'
import {
  CardMeta,
  CardMetaGrid,
  ManagementCard,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard'
import { EMPTY_VALUE } from 'constants/index'
import { routes } from 'constants/routes'
import { commonStrings, accountManagementStrings } from 'constants/strings'
import {
  EOrganizationMemberRole,
  EOrganizationMemberStatus,
  type TAdminAccount,
} from 'types'
import { getAccountOrganizationLabel } from './accountOrganizationDisplay'
import type { TTermsMemberConsentState } from 'types/terms'
import { TermsStateBadge } from './TermsStateBadge'

const buildAccountEditPath = (accountId: number) =>
  routes.ACCOUNT_MANAGEMENT_EDIT.replace(':accountId', String(accountId))

type AccountsCardListProps = {
  accounts: TAdminAccount[]
  loading: boolean
  emptyMessage?: string
  termsStateByAccountId?: Record<number, TTermsMemberConsentState>
  canEdit?: boolean
  className?: string
}

export function AccountsCardList({
  accounts,
  loading,
  emptyMessage = accountManagementStrings.states.empty,
  termsStateByAccountId = {},
  canEdit = true,
  className,
}: AccountsCardListProps) {
  const navigate = useNavigate()

  return (
    <ManagementCardList
      loading={loading}
      isEmpty={accounts.length === 0}
      loadingMessage={commonStrings.loading}
      emptyMessage={emptyMessage}
      className={className}
    >
      {accounts.map((account) => (
        <li key={account.id}>
          <ManagementCard
            onClick={
              canEdit
                ? () => navigate(buildAccountEditPath(account.id))
                : undefined
            }
            ariaLabel={`${account.name || account.email} ${commonStrings.actions.edit}`}
            title={account.name || EMPTY_VALUE}
            badges={
              <Pill
                tone={
                  account.status === EOrganizationMemberStatus.ACTIVE
                    ? 'success'
                    : 'neutral'
                }
                className="ml-auto"
              >
                {account.status === EOrganizationMemberStatus.ACTIVE
                  ? 'ACTIVE'
                  : 'PAUSED'}
              </Pill>
            }
          >
            <CardMetaGrid>
              <CardMeta
                label={accountManagementStrings.table.headers.role}
                value={
                  account.role === EOrganizationMemberRole.ADMIN
                    ? 'ADMIN'
                    : 'STANDARD'
                }
              />
              <CardMeta
                label={accountManagementStrings.table.headers.organization}
                value={
                  getAccountOrganizationLabel(account) || EMPTY_VALUE
                }
              />
            </CardMetaGrid>
            <CardMeta
              label={accountManagementStrings.table.headers.email}
              value={account.email}
            />
            <CardMeta
              label={accountManagementStrings.table.headers.terms}
              value={
                <TermsStateBadge state={termsStateByAccountId[account.id]} />
              }
            />
          </ManagementCard>
        </li>
      ))}
    </ManagementCardList>
  )
}
